import { useState } from "react";
import { useTranslation } from "react-i18next";
import { ContentSwitcher, Switch } from "@carbon/react";
import {
  ArrowDown,
  ArrowUp,
  CheckmarkOutline,
  Growth,
  Money,
  Time,
  Trophy,
} from "@carbon/icons-react";
import { KpiSummary } from "../../types";
import StatCard from "../carbon/StatCard/StatCard";
import { formatCurrency, formatPercentage } from "./utils/kpiHelpers";
import "./analyticsKpiRow.scss";

interface AnalyticsKpiRowProps {
  kpisByCurrency: KpiSummary[];
  /**
   * Controlled currency: the page owns the selection so every chart follows the switch. When
   * omitted the row keeps its own selection (first currency by default).
   */
  currency?: string;
  /** Called with the picked currency code when the user flips the currency switch. */
  onCurrencyChange?: (currency: string) => void;
}

/** Shown when the backend returns no currency at all (empty window); never summed or guessed. */
const EMPTY_KPI: KpiSummary = {
  currency: "USD",
  spend: 0,
  savings: 0,
  savingsPct: 0,
  awards: 0,
  offersReceived: 0,
  awardRatePct: 0,
  avgCycleDays: 0,
  onTimePct: 0,
  priorSpend: 0,
};

const AnalyticsKpiRow = ({
  kpisByCurrency,
  currency,
  onCurrencyChange,
}: AnalyticsKpiRowProps) => {
  const { t } = useTranslation();
  const [localCurrency, setLocalCurrency] = useState<string | null>(null);
  const selectedCurrency = currency ?? localCurrency;

  const kpi =
    kpisByCurrency.find((k) => k.currency === selectedCurrency) ??
    kpisByCurrency[0] ??
    EMPTY_KPI;
  const hasSwitch = kpisByCurrency.length > 1;
  const isEmpty = !kpi.spend;
  const dash = "—";

  const hasPrior = kpi.priorSpend > 0;
  const deltaPct = hasPrior
    ? ((kpi.spend - kpi.priorSpend) / kpi.priorSpend) * 100
    : 0;
  const trend = deltaPct > 0 ? "up" : deltaPct < 0 ? "down" : "flat";

  const spendFooter = isEmpty ? undefined : hasPrior ? (
    <p className={`analytics-kpi__delta analytics-kpi__delta--${trend}`}>
      {trend === "up" ? (
        <ArrowUp size={16} aria-hidden="true" />
      ) : trend === "down" ? (
        <ArrowDown size={16} aria-hidden="true" />
      ) : null}
      <span className="analytics-kpi__delta-pct">
        {formatPercentage(Math.abs(deltaPct))}
      </span>
      <span>{t("analytics.kpi.vsPrior")}</span>
    </p>
  ) : (
    <p className="analytics-kpi__delta">{t("analytics.kpi.noPrior")}</p>
  );

  const emptyCaption = t("analytics.kpi.noActivityCaption");

  return (
    <section className="analytics-kpi">
      {hasSwitch ? (
        <div className="analytics-kpi__toolbar">
          <p className="analytics-kpi__toolbar-label">
            {t("analytics.kpi.currency")}
          </p>
          <ContentSwitcher
            className="analytics-kpi__switcher"
            size="sm"
            selectedIndex={Math.max(
              0,
              kpisByCurrency.findIndex((k) => k.currency === kpi.currency)
            )}
            onChange={({ name }) => {
              setLocalCurrency(String(name));
              onCurrencyChange?.(String(name));
            }}
          >
            {kpisByCurrency.map((k) => (
              <Switch key={k.currency} name={k.currency} text={k.currency} />
            ))}
          </ContentSwitcher>
        </div>
      ) : null}

      {isEmpty ? (
        <p className="analytics-kpi__empty" role="status">
          {t("analytics.kpi.noActivity")}
        </p>
      ) : null}

      <div className="analytics-kpi__grid">
        <StatCard
          label={t("analytics.kpi.spend")}
          value={formatCurrency(kpi.spend, kpi.currency)}
          caption={isEmpty ? emptyCaption : undefined}
          footer={spendFooter}
          icon={Money}
        />
        <StatCard
          label={t("analytics.kpi.savings")}
          value={formatCurrency(kpi.savings, kpi.currency)}
          caption={
            isEmpty
              ? emptyCaption
              : t("analytics.kpi.savingsCaption", {
                  pct: formatPercentage(kpi.savingsPct),
                })
          }
          icon={Growth}
        />
        <StatCard
          label={t("analytics.kpi.awards")}
          value={kpi.awards.toLocaleString()}
          caption={
            isEmpty
              ? emptyCaption
              : t("analytics.kpi.awardsCaption", {
                  rate: formatPercentage(kpi.awardRatePct),
                  received: kpi.offersReceived.toLocaleString(),
                })
          }
          icon={Trophy}
        />
        <StatCard
          label={t("analytics.kpi.cycle")}
          value={
            isEmpty
              ? dash
              : `${kpi.avgCycleDays.toFixed(1)} ${t("analytics.days")}`
          }
          caption={isEmpty ? emptyCaption : undefined}
          icon={Time}
        />
        <StatCard
          label={t("analytics.kpi.onTime")}
          value={isEmpty ? dash : formatPercentage(kpi.onTimePct)}
          caption={isEmpty ? emptyCaption : t("analytics.kpi.onTimeCaption")}
          icon={CheckmarkOutline}
        />
      </div>
    </section>
  );
};

export default AnalyticsKpiRow;
